import { criteriaAnchorLibrary } from './criteriaAnchorLibrary';

// Default criteria for roles not in the anchor library
const defaultCriteria = [
  'Relevant experience and skills',
  'Communication and professionalism',
  'Reliability and work ethic',
  'Team fit and attitude',
  'Problem-solving under pressure',
];

// Maps lowercase role strings → criteriaAnchorLibrary keys
// Single source of truth — do not duplicate in other files
const ROLE_SLUG_TO_KEY = {
  'cafe manager':          'cafeManager',
  'café manager':          'cafeManager',
  'barista':               'barista',
  'chef':                  'chef',
  'head chef':             'chef',
  'sous chef':             'chef',
  'kitchen hand':          'kitchenHand',
  'kitchen hand / dish':   'kitchenHand',
  'retail assistant':      'retailAssistant',
  'retail manager':        'retailManager',
  'store manager':         'retailManager',
  'sales assistant':       'salesRepresentative',
  'sales representative':  'salesRepresentative',
  'sales rep':             'salesRepresentative',
  'customer service':      'customerServiceRep',
  'customer service representative': 'customerServiceRep',
  'office administrator':  'officeAdministrator',
  'administrator':         'officeAdministrator',
  'office admin':          'officeAdministrator',
  'office manager':        'officeAdministrator',
  'medical receptionist':  'medicalReceptionist',
  'receptionist':          'medicalReceptionist',
  'builder':               'builder',
  'carpenter':             'builder',
  'plumber':               'plumber',
  'electrician':           'electrician',
  'labourer':              'builder',
  'warehouse':             'warehouseStoreperson',
  'storeperson':           'warehouseStoreperson',
  'warehouse / storeperson': 'warehouseStoreperson',
  'driver':                'deliveryDriver',
  'delivery driver':       'deliveryDriver',
  'courier':               'deliveryDriver',
  'support worker':        'supportWorker',
  'care worker':           'supportWorker',
  'carer':                 'supportWorker',
  'ece teacher':           'eceTeacher',
  'early childhood':       'eceTeacher',
  'teacher aide':          'eceTeacher',
  'hairdresser':           'hairdresser',
  'barber':                'hairdresser',
};

function resolveAnchorKey(role) {
  if (!role) return null;
  const normalised = role.trim().toLowerCase();
  if (ROLE_SLUG_TO_KEY[normalised]) {
    return ROLE_SLUG_TO_KEY[normalised];
  }
  // Blended titles (slash- or comma-delimited) that are NOT an exact single-role
  // match are genuine multi-function roles — return null so getEnrichedCriteria
  // falls through to the API-generated, blend-aware criteria rather than forcing
  // a single function's static anchor block. Exact single-role slugs that contain
  // a slash (e.g. "warehouse / storeperson") are already resolved above, so this
  // only catches true blends.
  if (/[/,]/.test(normalised)) return null;
  // Partial match
  const entry = Object.entries(ROLE_SLUG_TO_KEY).find(
    ([k]) => normalised.includes(k) || k.includes(normalised)
  );
  return entry ? entry[1] : null;
}

/**
 * Returns criterion labels for a role.
 * Source of truth: criteriaAnchorLibrary criterionLabel values.
 * Falls back to defaultCriteria for unlisted roles.
 */
export function getCriteriaForRole(role) {
  const anchorKey = resolveAnchorKey(role);
  const roleData = anchorKey
    ? criteriaAnchorLibrary[anchorKey]
    : null;
  if (roleData?.anchors?.length) {
    return roleData.anchors.map(a => a.criterionLabel);
  }
  return defaultCriteria;
}

/**
 * Returns enriched criteria — each item:
 *   { label, anchors: {no,maybe,yes,strongYes} | null,
 *     complianceGates: array | null }
 * complianceGates carried on index 0 only for PDF builder.
 */
export function getEnrichedCriteria(role, apiCriteria = null) {
  const anchorKey = resolveAnchorKey(role);
  const roleData = anchorKey
    ? criteriaAnchorLibrary[anchorKey]
    : null;

  if (!roleData?.anchors?.length) {
    // Unlisted role — use API-generated criteria if available, otherwise use defaults
    if (apiCriteria?.length === 5) {
      return apiCriteria.map((criterion, i) => ({
        label: criterion.criterionLabel,
        anchors: {
          criterionLabel: criterion.criterionLabel,
          no: criterion.no ?? [],
          maybe: criterion.maybe ?? [],
          yes: criterion.yes ?? [],
          strongYes: criterion.strongYes ?? [],
        },
        complianceGates: i === 0 ? [{ label: 'Right to work in NZ', level: 'hard' }] : null,
      }));
    }
    // API criteria present but wrong count — use what we have
    if (apiCriteria?.length > 0) {
      return apiCriteria.map((criterion, i) => ({
        label: criterion.criterionLabel,
        anchors: {
          criterionLabel: criterion.criterionLabel,
          no: criterion.no ?? [],
          maybe: criterion.maybe ?? [],
          yes: criterion.yes ?? [],
          strongYes: criterion.strongYes ?? [],
        },
        complianceGates: i === 0 ? [{ label: 'Right to work in NZ', level: 'hard' }] : null,
      }));
    }
    // Final fallback — generic defaults, no anchors
    return defaultCriteria.map((label, i) => ({
      label,
      anchors: null,
      complianceGates: i === 0 ? [{ label: 'Right to work in NZ', level: 'hard' }] : null,
    }));
  }

  return roleData.anchors.map((anchor, i) => ({
    label: anchor.criterionLabel,
    anchors: anchor,
    complianceGates: i === 0
      ? (roleData.complianceGatesMeta ?? null)
      : null,
  }));
}
